import { getIsFaceCollageEnabledForScene } from "./getIsFaceCollageEnabledForScene.js";
import { getTrimmedClipDurationInSeconds } from "./getTrimmedClipDurationInSeconds.js";
import { getTransitionDuration, getTiming } from "./transitions-lib.js";
import { emojiSafeCharacterSplit } from "./text-lib.js";
import { toFrames, toSeconds } from "./frames-lib.js";
import { roundToThousandths } from "../numbers.js";
import { assertNever } from "../type-witchcraft.js";
export const MEMENTO_OUTRO_DURATION = 8;
/**
 * NOTE: This function does not and should not account for the transition time.
 */
export function getGreetingSceneIndividualMessageDurationSeconds(scene, messageText) {
    return (scene.secondsPerCharacter * emojiSafeCharacterSplit(messageText).length +
        scene.minimumMessageDisplayTimeSeconds);
}
/**
 * NOTE: This function does not and should not account for the transition time.
 */
export function getCircleGreetingSceneIndividualMessageDurationSeconds(
// this is just here to make sure the caller is using the correct scene
// type, it isn't used for anything else
_scene) {
    return 5; // static, 5 seconds
}
export function getIndividualClipDurationInFrames({ fps, theme, scene, clip, isLastClip, }) {
    let sceneTransitionDurationInFrames = 0;
    let betweenTransitionDurationInFrames = 0;
    if (theme) {
        const sceneTransitionResult = getTransitionDuration({
            exitTransition: scene.exitTransition,
            sceneTransition: theme.styles.sceneTransition,
            fps,
        });
        sceneTransitionDurationInFrames =
            sceneTransitionResult.transitionDurationInFrames;
        const betweenTransition = theme.styles.clips.betweenTransition;
        const betweenTransitionTiming = getTiming(betweenTransition, fps);
        betweenTransitionDurationInFrames =
            betweenTransitionTiming?.getDurationInFrames({ fps }) || 0;
    }
    const transitionDurationInFrames = isLastClip
        ? sceneTransitionDurationInFrames
        : betweenTransitionDurationInFrames;
    let clipDurationInFrames = toFrames(getTrimmedClipDurationInSeconds(clip), fps);
    let clipDurationWithTransitionInFrames = clipDurationInFrames + transitionDurationInFrames;
    // The final clip duration, with the transition, cannot be shorter than the
    // betweenTransition or we'll get errors from TransitionSeries. We're doing
    // this here so that everything call this function will agree on the duration.
    // Note that even though we add the transition duration to the clip duration,
    // which seems like it would make it impossible for it to be shorter than
    // betweenTransitionDurationInFrames, for the last clip we add the
    // sceneTransitionDurationInFrames instead, which could be shorter than
    // betweenTransitionDurationInFrames enough to make
    // clipDurationWithTransitionInFrames also shorter than
    // betweenTransitionDurationInFrames.
    if (clipDurationWithTransitionInFrames < betweenTransitionDurationInFrames) {
        const difference = betweenTransitionDurationInFrames - clipDurationInFrames;
        // add the same amount to each duration so they'll agree
        clipDurationInFrames += difference;
        clipDurationWithTransitionInFrames += difference;
    }
    return {
        clipDurationInFrames,
        clipDurationWithTransitionInFrames,
    };
}
export function getClipSeriesDurationSeconds({ theme, scene, clips, fps, }) {
    let transitionDurationInSeconds = 0;
    if (theme) {
        const transitionResult = getTransitionDuration({
            exitTransition: scene.exitTransition,
            sceneTransition: theme.styles.sceneTransition,
            fps,
        });
        transitionDurationInSeconds = transitionResult.transitionDurationInSeconds;
    }
    // We need to compute this in frames, and convert each individual clip
    // duration to frames before adding because that's what each scene component
    // will do (it will individually compute the duration of the clip and convert
    // that to frames). If we reduce this in seconds we'll get accumulating e.g.
    // off-by-one errors.
    const clipsDurationFrames = clips.reduce((totalDurationFrames, clip, index) => {
        const isLastClip = index === clips.length - 1;
        const { clipDurationInFrames } = getIndividualClipDurationInFrames({
            fps,
            clip,
            isLastClip,
            theme,
            scene,
        });
        return totalDurationFrames + clipDurationInFrames;
    }, 0);
    // Since we need the clip series to overlap with the scene following it
    // we need to include the scene transition time
    return roundToThousandths(toSeconds(clipsDurationFrames, fps) + transitionDurationInSeconds);
}
function getGreeting(scene, renderConfig) {
    const sceneVariant = scene.variant;
    if (sceneVariant === "intro") {
        return renderConfig.introGreeting;
    }
    else if (sceneVariant === "outro") {
        return renderConfig.outroGreeting;
    }
    else {
        assertNever(sceneVariant);
    }
}
export function getSceneDurationInSeconds(scene, renderConfig, theme) {
    const isFaceCollageEnabled = getIsFaceCollageEnabledForScene("faceCollage" in scene ? scene.faceCollage : undefined, renderConfig);
    const fps = renderConfig.videoFrame.fps;
    const { transitionDurationInSeconds } = getTransitionDuration({
        exitTransition: scene.exitTransition,
        sceneTransition: theme.styles.sceneTransition,
        fps,
    });
    switch (scene.type) {
        case "greeting":
        case "typeOnGreeting": {
            const greeting = getGreeting(scene, renderConfig);
            if (!greeting.enabled) {
                return 0;
            }
            else {
                return greeting.messages.reduce((duration, text) => duration +
                    getGreetingSceneIndividualMessageDurationSeconds(scene, text), 
                // Include the transition time since the text will be displayed after
                // the transition. If the text started during the transition, it would
                // be obscured/unreadable by the transition. This assumes that the
                // greeting scene will delay the display of text for
                // `transitionDurationInSeconds`.
                transitionDurationInSeconds);
            }
        }
        case "greetingLinesAsScenes": {
            const greeting = getGreeting(scene, renderConfig);
            if (!greeting.enabled) {
                return 0;
            }
            const textDurationSeconds = greeting.messages.reduce((duration, text) => duration +
                getGreetingSceneIndividualMessageDurationSeconds(scene, text) +
                // We're including the transition time because GreetingLinesAsScenes
                // adds the transition time to each message. If that changes, then
                // make the same change here.
                transitionDurationInSeconds, 0);
            // We don't include the transition time before this scene (only between
            // messages) like we do for "greeting" and "typeOnGreeting" scenes because
            // we don't delay the start of the text for the transition. If that ever
            // happens, we'll have to adjust this calculation.
            return (textDurationSeconds -
                (greeting.messages.length - 1) * transitionDurationInSeconds);
        }
        case "clip-series-basic":
            return getClipSeriesDurationSeconds({
                theme,
                scene,
                clips: renderConfig.clips,
                fps,
            });
        case "popHeartsOutro":
            if (!renderConfig.branding.enabled) {
                return 0;
            }
            return MEMENTO_OUTRO_DURATION;
        case "outro":
            if (!renderConfig.branding.enabled) {
                return 0;
            }
            return MEMENTO_OUTRO_DURATION;
        case "lottieOutro":
            if (!renderConfig.branding.enabled) {
                return 0;
            }
            return scene.durationSeconds;
        case "lottie":
            return scene.durationSeconds;
        case "imageSequence":
            return scene.frameCount / renderConfig.videoFrame.fps;
        case "inkyGuestBookIntro":
            if (isFaceCollageEnabled) {
                return (scene.faceCollage?.faceCollageSceneDurationSeconds ||
                    scene.durationSeconds);
            }
            return scene.durationSeconds;
        case "birthdayCakeIntro":
            if (isFaceCollageEnabled) {
                return (scene.faceCollage?.faceCollageSceneDurationSeconds ||
                    scene.durationSeconds);
            }
            else {
                return scene.durationSeconds;
            }
        case "circleGreeting":
            const greeting = scene.variant === "intro"
                ? renderConfig.introGreeting
                : renderConfig.outroGreeting;
            return greeting.enabled
                ? greeting.messages.length *
                    getCircleGreetingSceneIndividualMessageDurationSeconds(scene) +
                    // Exclude the intro and outro transition duration
                    (greeting.messages.length - 2) * transitionDurationInSeconds
                : 0;
        case "textTransition":
            return scene.durationSeconds;
        case "generalSolidIntro":
            return scene.durationSeconds;
        case "bigCongratsIntro":
            return scene.durationSeconds;
        case "schoolSpiritIntro":
            return scene.durationSeconds;
        case "meadowFlowersIntro":
            return scene.durationSeconds;
        case "mountainViewIntro":
            return scene.durationSeconds;
        case "paintDaubsIntro":
            return scene.durationSeconds;
        case "birthdayCandlesIntro":
            if (isFaceCollageEnabled) {
                return (scene.faceCollage?.faceCollageSceneDurationSeconds ||
                    scene.durationSeconds);
            }
            return scene.durationSeconds;
    }
}
